import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Paper,
  Divider,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  TextField,
  Link,
  DialogActions,
} from "@mui/material";
import { Add, Visibility, Edit, Delete, CloudDownload, Download, Close as CloseIcon, CalendarToday as CalendarTodayIcon, PhotoLibrary as PhotoLibraryIcon, PictureAsPdf as PictureAsPdfIcon, Description as DescriptionIcon, YouTube as YouTubeIcon, Link as LinkIcon } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAutocomplete from "components/MDAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { IconTableExport } from "@tabler/icons-react";
import handleExport from "utils/exportData";
import PropTypes from "prop-types";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

function EventsList() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [filters, setFilters] = useState({
    event_id: null,
    user_id: null,
    district_id: null,
  });

  const [viewFileUrl, setViewFileUrl] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, url: null, submissionId: null });
  const [deleting, setDeleting] = useState(false);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  // Fetch Dropdown Data
  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchAllUsers = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/role3`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: fetchAllUsers,
    retry: false,
  });

  const fetchEvents = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/all`, { withCredentials: true });
    return res.data.data;
  };
  const { data: eventsList } = useQuery({ queryKey: ["events"], queryFn: fetchEvents, retry: false, });

  const districtOptions = useMemo(() => (Array.isArray(districtData) ? districtData : []), [districtData]);
  const usersOptions = useMemo(() => (Array.isArray(usersData) ? usersData.map(u => ({ title: u.title, value: u.value })) : []), [usersData]);
  const eventOptions = useMemo(() => (Array.isArray(eventsList) ? eventsList.map(e => ({ title: e.title, value: e.value })) : []), [eventsList]);

  const DateCell = ({ cell }) => (cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "N/A");
  DateCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const DescriptionCell = ({ cell }) => {
    const desc = cell.getValue() || "";
    return (
      <Tooltip title={desc} arrow>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {desc.length > 50 ? desc.substring(0, 50) + "..." : desc}
        </Typography>
      </Tooltip>
    );
  };
  DescriptionCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const UploadsCell = ({ cell, row }) => {
    const uploads = cell.getValue() || [];
    if (uploads.length === 0) return "-";
    return (
      <Box display="flex" flexDirection="column" gap={0.5} py={0.5}>
        {uploads.map((url, idx) => {
          const cleanUrl = url.split("?")[0].toLowerCase();
          const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(cleanUrl);
          const isPdf = /\.pdf$/i.test(cleanUrl);
          const isWord = /\.(doc|docx)$/i.test(cleanUrl);

          return (
            <Box key={idx} display="flex" gap={1} alignItems="center">
              {isImage ? (
                <PhotoLibraryIcon sx={{ fontSize: "1rem", color: "#4caf50" }} />
              ) : isPdf ? (
                <PictureAsPdfIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              ) : isWord ? (
                <DescriptionIcon sx={{ fontSize: "1rem", color: "#2196f3" }} />
              ) : null}
              <Tooltip title={`View ${isImage ? "Photo" : isPdf ? "PDF" : isWord ? "Word" : "File"}`}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handlePreview(url)}
                  sx={{ bgcolor: "#e3f2fd", "&:hover": { bgcolor: "#bbdefb" }, width: 24, height: 24 }}
                >
                  <Visibility sx={{ fontSize: "0.875rem" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleDownload(url)}
                  sx={{ bgcolor: "#e8f5e9", "&:hover": { bgcolor: "#c8e6c9" }, width: 24, height: 24 }}
                >
                  <CloudDownload sx={{ fontSize: "0.875rem" }} />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Delete File">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialog({ open: true, url, submissionId: row.original.id })}
                    sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" }, width: 24, height: 24 }}
                  >
                    <Delete sx={{ fontSize: "0.875rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };
  UploadsCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const VideoCell = ({ cell }) => {
    const url = cell.getValue();
    if (!url) return "-";
    const urls = url.split(",").map((u) => u.trim());
    return (
      <Box display="flex" gap={1} flexWrap="wrap">
        {urls.map((u, i) => {
          const isYouTube = u.includes("youtube.com") || u.includes("youtu.be");
          const isDrive = u.includes("drive.google.com");

          return (
            <Tooltip
              title={isYouTube ? "Watch on YouTube" : isDrive ? "View on Google Drive" : "Visit Link"}
              key={i}
              arrow
            >
              <IconButton
                component="a"
                href={u}
                target="_blank"
                rel="noopener"
                size="small"
                sx={{
                  color: isYouTube ? "#f44336" : isDrive ? "#1A73E8" : "#757575",
                  bgcolor: isYouTube ? "#ffebee" : isDrive ? "#e3f2fd" : "#f5f5f5",
                  "&:hover": {
                    bgcolor: isYouTube ? "#ffcdd2" : isDrive ? "#bbdefb" : "#eeeeee",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                {isYouTube ? (
                  <YouTubeIcon sx={{ fontSize: "1.25rem" }} />
                ) : isDrive ? (
                  <DescriptionIcon sx={{ fontSize: "1.1rem" }} />
                ) : (
                  <LinkIcon sx={{ fontSize: "1.1rem" }} />
                )}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
    );
  };
  VideoCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const DateFilter = ({ column }) => {
    const [open, setOpen] = useState(false);
    const filterValue = column.getFilterValue();
    const value = filterValue ? dayjs(filterValue, "DD-MM-YYYY") : null;

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(newValue) => {
            column.setFilterValue(newValue ? dayjs(newValue).format("DD-MM-YYYY") : undefined);
          }}
          slotProps={{
            textField: {
              onClick: () => setOpen(true),
              variant: "standard",
              placeholder: "Filter Date",
              InputProps: {
                endAdornment: (
                  <React.Fragment>
                    {value && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          column.setFilterValue(undefined);
                        }}
                      >
                        <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => setOpen(true)}>
                      <CalendarTodayIcon fontSize="small" sx={{ color: "#fff" }} />
                    </IconButton>
                  </React.Fragment>
                ),
              },
              sx: {
                minWidth: "120px",
                "& .MuiInputBase-root": {
                  color: "#fff",
                  fontSize: "12px",
                  cursor: "pointer",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  fontSize: "13px",
                  cursor: "pointer",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#e0e0e0",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  };
  DateFilter.propTypes = {
    column: PropTypes.object.isRequired,
  };

  const ActionCell = ({ row }) => (
    <Box display="flex" gap={1}>
      <Tooltip title="Edit Submission">
        <IconButton
          size="small"
          color="warning"
          onClick={() => navigate(`/events/edit/${row.original.id}/${row.original.user_id}`)}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.any,
        user_id: PropTypes.any,
      }).isRequired,
    }).isRequired,
  };

  const fetchEventsList = async (page = 0, size = 10, filterValues = filters, colFilters = columnFilters, sortValues = sorting) => {
    if (!meData) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: size,
        event_id: filterValues.event_id || "",
        user_id: isAdmin ? (filterValues.user_id || "") : meData.user_id,
        district_id: filterValues.district_id || "",
        columnFilters: JSON.stringify(colFilters),
      });

      if (sortValues.length > 0) {
        queryParams.append("sortField", sortValues[0].id);
        queryParams.append("sortOrder", sortValues[0].desc ? "desc" : "asc");
      }

      const endpoint = isAdmin ? "/event-submissions/list" : `/event-submissions/list/${meData.user_id}`;
      const url = `${process.env.REACT_APP_API_URL}${endpoint}?${queryParams.toString()}`;

      const res = await axios.get(url, { withCredentials: true });
      if (res.data.status === 200) {
        setData(res.data.data || []);
        setRowCount(res.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (meData) {
      fetchEventsList(pagination.pageIndex, pagination.pageSize, filters, columnFilters, sorting);
    }
  }, [pagination.pageIndex, pagination.pageSize, meData, columnFilters, sorting]);

  const handleSearch = () => {
    setPagination({ ...pagination, pageIndex: 0 });
    fetchEventsList(0, pagination.pageSize, filters, columnFilters, sorting);
  };

  const handleReset = () => {
    const resetFilters = { event_id: null, user_id: null, district_id: null };
    setFilters(resetFilters);
    setPagination({ ...pagination, pageIndex: 0 });
    fetchEventsList(0, pagination.pageSize, resetFilters, [], []);
  };

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.split("?")[0].toLowerCase();
    const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");

    if (isWord) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      setViewFileUrl(officeUrl);
    } else {
      setViewFileUrl(url);
    }
  };

  const handleDownload = async (url) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = url.split("/").pop().split("?")[0];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: "Download started!", severity: "success" });
    } catch (error) {
      console.error("Download failed", error);
      setSnackbar({ open: true, message: "Download failed.", severity: "error" });
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        event_id: filters.event_id || "",
        user_id: isAdmin ? (filters.user_id || "") : meData.user_id,
        district_id: filters.district_id || "",
        columnFilters: JSON.stringify(columnFilters),
      });

      const endpoint = isAdmin ? "/event-submissions/list" : `/event-submissions/list/${meData.user_id}`;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}?${queryParams.toString()}`, { withCredentials: true });

      if (res.data.status === 200) {
        await handleExport(res.data.data, "Event_Submissions_Export");
        setSnackbar({ open: true, message: "Excel exported successfully!", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Export failed.", severity: "error" });
    }
    setLoading(false);
  };

  const handleDeleteFile = async () => {
    if (!deleteDialog.url || !deleteDialog.submissionId) return;
    
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/event-submissions/delete-file/${deleteDialog.submissionId}`,
        {
          data: { url: deleteDialog.url },
          withCredentials: true
        }
      );

      if (res.data.status === 200) {
        setSnackbar({ open: true, message: "File deleted successfully!", severity: "success" });
        // Refresh data
        fetchEventsList(pagination.pageIndex, pagination.pageSize, filters, columnFilters, sorting);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || "Failed to delete file.", 
        severity: "error" 
      });
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, url: null, submissionId: null });
    }
  };

  const columns = useMemo(() => {
    return [
      ...(isAdmin
        ? [
          {
            accessorKey: "user_name",
            header: "User",
            size: 150,
            filterVariant: "autocomplete",
            filterSelectOptions: usersOptions.map((u) => u.title),
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
          },
          {
            accessorKey: "district_name",
            header: "District",
            size: 150,
            filterVariant: "select",
            filterSelectOptions: districtOptions.map((d) => d.title),
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
          },
        ]
        : []),
      {
        accessorKey: "event_name",
        header: "Event Name",
        size: 200,
        filterVariant: "select",
        filterSelectOptions: eventOptions.map((d) => d.title),
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "no_of_participants",
        header: "Participants",
        size: 200,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "conducted_date",
        header: "Conducted Date",
        size: 220,
        Cell: DateCell,
        Filter: DateFilter,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 250,
        Cell: DescriptionCell,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "uploads",
        header: "Uploads",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: UploadsCell,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "drive_url",
        header: "Event Video",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: VideoCell,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      ...(!isAdmin
        ? [
          {
            accessorKey: "actions",
            header: "Actions",
            size: 100,
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ActionCell,
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
          },
        ]
        : []),
    ];
  }, [isAdmin, navigate, usersOptions, districtOptions, eventOptions]);

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card id="delete-account" sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} pb={3}>
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                Events Submissions List
              </MDTypography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }} display="flex" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => navigate("/events/add")}
                startIcon={<Add />}
                sx={{
                  background: 'linear-gradient(195deg, #0b6750, #0b6750)',
                  "&:hover": { background: 'linear-gradient(195deg, #0b6750, #0b6750)' }
                }}
              >
                {isAdmin ? "Manage Event" : "Event Submission"}
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Select Event"
                name="event_filter"
                options={eventOptions}
                value={filters.event_id}
                onChange={(val) => setFilters({ ...filters, event_id: val })}
                isFilter
              />
            </Grid>
            {isAdmin && (
              <>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Select User"
                    name="user_filter"
                    options={usersOptions}
                    value={filters.user_id}
                    onChange={(val) => setFilters({ ...filters, user_id: val })}
                    isFilter
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Select District"
                    name="district_filter"
                    options={districtOptions}
                    value={filters.district_id}
                    onChange={(val) => setFilters({ ...filters, district_id: val })}
                    isFilter
                  />
                </Grid>
              </>
            )}
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            {/* Search Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={handleSearch}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>

            {/* Cancel Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={handleReset}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Box>
        <Grid container spacing={5}>
          <Grid item size={{ xs: 12, md: 12 }} className="listTable">
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Paper
                elevation={2}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  p: 2,
                }}
              >
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  getRowId={(row) => row.id}
                  rowCount={rowCount}
                  enableRowNumbers
                  enableStickyHeader
                  manualPagination
                  manualFiltering
                  manualSorting
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  onColumnFiltersChange={setColumnFilters}
                  state={{
                    pagination,
                    sorting,
                    columnFilters,
                    isLoading: loading,
                  }}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  initialState={{ showColumnFilters: false }}
                  displayColumnDefOptions={{
                    "mrt-row-numbers": {
                      size: 40,
                      maxSize: 40,
                      position: "left",
                      muiTableHeadCellProps: { align: "center" },
                      muiTableBodyCellProps: { align: "center" },
                    },
                  }}
                  layoutMode="grid"
                  muiTableHeadProps={{
                    sx: {
                      padding: "0px", // won't apply directly to <thead>, but useful for general structure
                      backgroundColor: "#011F3A", // background on <thead> element itself
                    },
                  }}
                  enableColumnActions={false}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      // fontSize: '16px',
                      fontWeight: "bold",
                      position: "sticky", // Make header sticky
                      top: 0, // Stick it at the top
                      zIndex: 1, // Ensure it stays
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                        "::placeholder": {
                          color: "#e0e0e0", // Default (blurred) placeholder color
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                        color: "#ffffff", // Focused placeholder color
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#e0e0e0",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff",
                      },

                      "& .MuiCheckbox-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px", // reduce from default 16px
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                    },
                  })}
                  renderTopToolbarCustomActions={({ table }) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <Button
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                              transform: "scale(1.05)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excelll"}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={Boolean(viewFileUrl)} onClose={() => setViewFileUrl(null)} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          File Preview
          <IconButton onClick={() => setViewFileUrl(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '60vh', p: 1 }}>
          {viewFileUrl && (
            (/\.(jpg|jpeg|png|webp|gif|svg)$/i.test(viewFileUrl.split("?")[0]) && !viewFileUrl.includes("officeapps.live.com")) ? (
              <Box component="img" src={viewFileUrl} sx={{ width: "100%", height: "auto", borderRadius: 2 }} />
            ) : (
              <iframe
                src={viewFileUrl}
                width="100%"
                height="600px"
                style={{ border: "none" }}
                title="File Preview"
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setViewFileUrl(null)} color="dark">Close</MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Are you sure you want to delete this file? This action will remove the file from the submission and Vercel storage.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton 
            onClick={() => setDeleteDialog({ ...deleteDialog, open: false })} 
            color="dark" 
            disabled={deleting}
          >
            Cancel
          </MDButton>
          <MDButton 
            onClick={handleDeleteFile} 
            color="error" 
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Confirm Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default EventsList;
